import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useManualQuery, useMutation } from "graphql-hooks";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
  Avatar,
  TextField,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Radio,
  Rating,
  RadioGroup,
  FormControlLabel,
  Collapse,
  Divider,
  Link,
} from "@mui/material";
import AlertPopup from "./components/AlertPopup";
import FloatingMenu from "./components/FloatingMenu";
import EmptyState from "./components/EmptyState";
import {
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowUpward,
  FilterListOutlined,
  ArrowDownward,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";

// Import toy images
import photo1 from "./assets/ToysList/photo1.png";
import photo2 from "./assets/ToysList/photo2.png";
import photo3 from "./assets/ToysList/photo3.png";
import photo4 from "./assets/ToysList/photo4.png";
import photo5 from "./assets/ToysList/photo5.png";
import photo6 from "./assets/ToysList/photo6.png";
import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

import { MY_TOY_LIST } from "./graphQL/toy_qry";
import { DELETE_TOY_DATA_BY_UUID } from "./graphQL/toy_qry";

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const ToysList = () => {
  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(833); // Use 833px breakpoint for mobile size
  const [statusFilter, setStatusFilter] = useState("All");
  const [viewFilter, setViewFilter] = useState("All");
  const [mobileSortBy, setMobileSortBy] = useState("publishedOn");
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("publishedOn");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [toys, setToys] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const formatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });

  const dayFormat = day => {
    const dayDate = new Date(day);
    const dayFormat = formatter.formatToParts(dayDate);
    const newFormat =
      dayFormat.find(a => a.type == "month").value +
      " " +
      dayFormat.find(a => a.type == "day").value +
      ", " +
      dayFormat.find(a => a.type == "year").value;
    return newFormat;
  };

  const [getMyToyList] = useManualQuery(MY_TOY_LIST);
  const [deleteToyMutate] = useMutation(DELETE_TOY_DATA_BY_UUID);

  // Popup states
  const [showPopup, setShowPopup] = useState(false);
  const [popupConfig, setPopupConfig] = useState({
    title: "",
    message: "",
    showCheckCircle: false,
    confirmText: "Close",
    cancelText: "CANCEL",
    onConfirm: null,
  });

  const FetchToyList = async () => {
    const {
      loading: loadingToyList,
      error: errorToyList,
      data: toyList,
    } = await getMyToyList();
    if (!loadingToyList && toyList) {
      console.log(toyList);
      setToys(
        toyList.myToyList.map(toy => ({
          id: toy.toyId,
          uuid: toy.uuid,
          photo: toy.toyImages[0]?.fileName || "",
          title: toy.title,
          price: toy.sellingPrice,
          originalPrice: toy.originalPrice,
          createdOn: dayFormat(toy.createDate),
          publishedOn: toy.publishedDate ? dayFormat(toy.publishedDate) : "Draft",
          status: toy.status,
          ratingNo: toy.ratingNo,
        }))
      );
      setIsLoaded(true);
    } else {
      console.log("error: ", errorToyList);
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    (async () => FetchToyList())();
  }, []);

  // Alert popup state
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    toyId: null,
  });
  const [deleteAlertPopup, setDeleteAlertPopup] = useState({
    open: false,
    toyUuid: null,
  });

  useEffect(() => {
    if (isMobile && rowsPerPage === 10) setRowsPerPage(5);
    else if (!isMobile && rowsPerPage === 5) setRowsPerPage(10);
  }, [rowsPerPage, isMobile, isTablet]);

  // Handle click outside to close sort panel in Mobile
  useEffect(() => {
    const handleClickOutside = event => {
      // Check if click is outside the sort panel container
      const sortPanel = document.querySelector("[data-sort-panel]");
      if (sortPanel && !sortPanel.contains(event.target) && sortPanelOpen) {
        setSortPanelOpen(false);
      }
    };

    if (sortPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sortPanelOpen]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // Filter change handlers
  const handleStatusFilterChange = event => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleViewFilterChange = event => {
    setViewFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleMobileSortChange = event => {
    const sortValue = event.target.value;
    setMobileSortBy(sortValue);
    handleRequestSort(sortValue);
    setPage(0); // Reset to first page when sort changes
  };

  // Popup handlers
  const handlePublishClick = toyId => {
    setAlertPopup({
      open: true,
      type: "publish",
      toyId: toyId,
    });
  };

  const handleUnpublishClick = toyId => {
    setAlertPopup({
      open: true,
      type: "unpublish",
      toyId: toyId,
    });
  };

  const handleConfirmAction = () => {
    if (alertPopup.type === "publish") {
      // Update toy to published
      setToys(prevToys =>
        prevToys.map(toy =>
          toy.id === alertPopup.toyId
            ? {
                ...toy,
                publishedOn: new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                }),
              }
            : toy
        )
      );
    } else if (alertPopup.type === "unpublish") {
      // Update toy to draft
      setToys(prevToys =>
        prevToys.map(toy =>
          toy.id === alertPopup.toyId ? { ...toy, publishedOn: "Draft" } : toy
        )
      );
    }
    setAlertPopup({ open: false, type: "", toyId: null });
  };

  const handleCancelAction = () => {
    setAlertPopup({ open: false, type: "", toyId: null });
  };

  const handleDeleteCancelAction = () => {
    setDeleteAlertPopup({ open: false, type: "", toyUuid: null });
  };

  // Sorting handler
  const handleRequestSort = property => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setMobileSortBy(property);
  };

  // Filter and sort data
  const getFilteredData = () => {
    let filteredData = toys;

    // Filter by Status
    if (statusFilter !== "All") {
      if (statusFilter === "Draft") {
        filteredData = filteredData.filter(toy => toy.publishedOn === "Draft");
      } else {
        filteredData = filteredData.filter(
          toy => toy.status === statusFilter && toy.publishedOn !== "Draft"
        );
      }
    }

    // Filter by View (Published/Unpublished)
    if (viewFilter !== "All") {
      if (viewFilter === "Published") {
        filteredData = filteredData.filter(toy => toy.publishedOn !== "Draft");
      } else if (viewFilter === "Draft") {
        filteredData = filteredData.filter(toy => toy.publishedOn === "Draft");
      }
    }

    // Sort data
    if (orderBy) {
      filteredData.sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        // Handle price sorting (remove $ and convert to number)
        if (orderBy === "price") {
          aValue = parseFloat(aValue.replace("$", ""));
          bValue = parseFloat(bValue.replace("$", ""));
        }

        // Handle title sorting (case insensitive)
        if (orderBy === "title") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Handle date sorting for createdOn and publishedOn
        if (orderBy === "createdOn" || orderBy === "publishedOn") {
          if (aValue === "Draft") aValue = new Date(0);
          else aValue = new Date(aValue);

          if (bValue === "Draft") bValue = new Date(0);
          else bValue = new Date(bValue);
        }

        // Handle status sorting (case insensitive)
        if (orderBy === "status") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Handle rating number sorting
        if (orderBy === "ratingNo") {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (aValue < bValue) {
          return order === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  };

  const filteredData = getFilteredData();

  // Get current page data from filtered results
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  function handleDeleteToy(toy) {
    setDeleteAlertPopup({
      open: true,
      toyUuid: toy.uuid,
    });
  }

  const handleDeleteToyAction = async () => {
    setDeleteAlertPopup({ open: false, type: "", toyUuid: null });

    const { data: deleteToyMutateData, error: deleteToyMutateError } =
      await deleteToyMutate({
        variables: {
          toyUuid: deleteAlertPopup.toyUuid,
        },
      });
    if (deleteToyMutateError) {
      console.log(deleteToyMutateError);
      setPopupConfig({
        title: "Ooops!",
        message: deleteToyMutateError,
        confirmText: "OK",
        cancelText: "",
        onConfirm: null,
      });
      setShowPopup(true);
    } else {
      if (deleteToyMutateData.deleteToyByUuid.success) {
        setToys(prevToys =>
          prevToys.filter(toy => toy.uuid !== deleteAlertPopup.toyUuid)
        );
      } else {
        setPopupConfig({
          title: "Ooops!",
          message: deleteToyMutateData.deleteToyByUuid.msg,
          confirmText: "OK",
          cancelText: "",
          onConfirm: null,
        });
        setShowPopup(true);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>R3PLAY - Sold Toys</title>
        <meta
          name="description"
          content="Review the toys you’ve successfully sold on R3PLAY"
        />
        <meta
          property="keywords"
          content="Sold toys, toy sales, toy transactions, seller dashboard"
        />
      </Helmet>
      {/* Mobile View - Only shows on screens < 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "16px",
          display: "none",
          "@media (max-width: 833px)": {
            display: "block",
          },
        }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            p: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#333",
              mb: 1,
            }}
          >
            My Toys for Sale
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#666",
              mb: 2,
            }}
          >
            Manage and edit your uploaded toys
          </Typography>

          {/* Mobile Filter Controls */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: "row",
            }}
          >
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "14px",
                  "&.Mui-focused": {
                    color: "#666",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
              SelectProps={{
                IconComponent: ArrowDropDown,
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="For Sale">For Sale</MenuItem>
              <MenuItem value="Sold">Sold</MenuItem>
              <MenuItem value="Shipped">Shipped</MenuItem>
              <MenuItem value="Received">Received</MenuItem>
              <MenuItem value="Accepted">Accepted</MenuItem>
              {/* --- Divider --- */}
              {/* <MenuItem disabled sx={{ padding: 0 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "1px",
                      backgroundColor: "rgba(0,0,0,0.12)",
                      // margin: "4px 0",
                    }}
                  />
                </MenuItem> */}

              <MenuItem value="Draft">Draft</MenuItem>
            </TextField>

            {/* <TextField
              select
              label="View"
              value={viewFilter}
              onChange={handleViewFilterChange}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "14px",
                  "&.Mui-focused": {
                    color: "#666",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
              SelectProps={{
                IconComponent: ArrowDropDown,
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Published">Published</MenuItem>
              <MenuItem value="Draft">Draft</MenuItem>
            </TextField> */}
          </Box>
        </Box>

        {/* Mobile Sort Panel */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
            width: "100%",
          }}
        >
          <Box sx={{ width: "200px", position: "relative" }} data-sort-panel>
            <TextField
              label="Sort by"
              value={
                mobileSortBy
                  ? (() => {
                      const options = {
                        title: "Title",
                        price: "Price",
                        createdOn: "Created on",
                        publishedOn: "Published on",
                        status: "Status",
                        ratingNo: "Rating",
                      };
                      return options[mobileSortBy] || "";
                    })()
                  : ""
              }
              onClick={() => setSortPanelOpen(!sortPanelOpen)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {mobileSortBy &&
                      (order === "desc" ? (
                        <ArrowDownward sx={{ fontSize: 20, color: "#666" }} />
                      ) : (
                        <ArrowUpward sx={{ fontSize: 20, color: "#666" }} />
                      ))}
                    {sortPanelOpen ? (
                      <ArrowDropUp sx={{ color: "#666" }} />
                    ) : (
                      <ArrowDropDown sx={{ color: "#666" }} />
                    )}
                  </Box>
                ),
              }}
              sx={{
                width: "200px",
                cursor: "pointer",
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  paddingRight: "8px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d0d0d0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "14px",
                  "&.Mui-focused": {
                    color: "#666",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                  pr: 0,
                },
                "& .MuiOutlinedInput-input": {
                  cursor: "pointer",
                },
              }}
            />

            {/* Sort options panel */}
            {sortPanelOpen && (
              <Box
                sx={{
                  position: "absolute",
                  top: "40px",
                  left: 0,
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #e0e0e0",
                  borderTop: "none",
                  borderRadius: "0 0 4px 4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                }}
              >
                <RadioGroup
                  value={mobileSortBy}
                  onChange={e => {
                    setMobileSortBy(e.target.value);
                    setOrder("desc");
                    setOrderBy(e.target.value);
                    setPage(0);
                    //setSortPanelOpen(false);
                  }}
                >
                  {[
                    { value: "title", label: "Title" },
                    { value: "price", label: "Price" },
                    { value: "createdOn", label: "Created on" },
                    { value: "publishedOn", label: "Published on" },
                    { value: "status", label: "Status" },
                    { value: "ratingNo", label: "Rating" },
                  ].map(option => (
                    <Box
                      key={option.value}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1,
                        pl: 0,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <FormControlLabel
                        value={option.value}
                        control={
                          <Radio
                            size="small"
                            sx={{
                              color: "#666",
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "14px", color: "#333" }}>
                            {option.label}
                          </Typography>
                        }
                        sx={{ margin: 0, flex: 1 }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "55px",
                          justifyContent: "space-between",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            setMobileSortBy(option.value);
                            setOrder("desc");
                            setOrderBy(option.value);
                            setPage(0);
                            setSortPanelOpen(false);
                          }}
                          sx={{
                            p: 0.25,
                            backgroundColor:
                              mobileSortBy === option.value && order === "desc"
                                ? "#fce4ec"
                                : "transparent",
                          }}
                        >
                          <ArrowDownward
                            fontSize="small"
                            sx={{ fontSize: 20, color: "#666" }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            setMobileSortBy(option.value);
                            setOrder("asc");
                            setOrderBy(option.value);
                            setPage(0);
                            setSortPanelOpen(false);
                          }}
                          sx={{
                            p: 0.25,
                            backgroundColor:
                              mobileSortBy === option.value && order === "asc"
                                ? "#fce4ec"
                                : "transparent",
                          }}
                        >
                          <ArrowUpward
                            fontSize="small"
                            sx={{ fontSize: 20, color: "#666" }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </RadioGroup>
              </Box>
            )}
          </Box>
        </Box>

        {/* Mobile Cards */}
        {!isLoaded ? (
          <Box p={4} textAlign="center">Loading...</Box>
        ) : isLoaded && toys.length === 0 ? (
          <EmptyState 
            type="listings"
            title="No Toys Available"
            description="You haven't listed any toys yet."
          />
        ) : (
          <>
            {paginatedData.map((toy, index) => (
          <Box
            key={toy.id}
            sx={{
              backgroundColor: index % 2 === 0 ? "#FDECF0" : "white", //#FEF3F5
              borderRadius: "8px",
              mb: 2,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableBody>
                {/* Row 1: Image + Title */}
                <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <TableCell sx={{ py: 2, width: "125px", border: "none" }}>
                    <Avatar
                      src={toy.photo}
                      alt={toy.title}
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "8px",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 2, border: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: "500",
                        lineHeight: 1.4,
                      }}
                    >
                      {toy.title}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Row 2: Created On + Published On */}
                <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <TableCell sx={{ py: 2, border: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      Created on
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                      }}
                    >
                      {toy.createdOn}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2, border: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      Published on
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                      }}
                    >
                      {toy.publishedOn === "Draft" ? "" : toy.publishedOn}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Row 3: Price + Status */}
                <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <TableCell sx={{ py: 2, border: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      Price
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {toy.price}
                      </Typography>
                      {toy.originalPrice && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#999",
                            fontWeight: "400",
                            textDecoration: "line-through",
                          }}
                        >
                          {toy.originalPrice}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 2,
                      border: "none",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      Status
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "white",
                        color: "#333",
                        border: "1px solid #000",
                        fontSize: "12px",
                        fontWeight: "500",
                        width: "80px",
                        height: "30px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      {toy.publishedOn === "Draft" ? "Draft" : toy.status}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Row 4: Rating + Actions */}
                <TableRow>
                  <TableCell sx={{ pt: 2, pb: 0, border: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      Buyer's Rating
                    </Typography>
                    <StyledTooltip title={toy.ratingNo > 0 ? `${toy.ratingNo} of 5` : ""}>
                      {toy.ratingNo > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-start",
                          }}
                        >
                          <Rating
                            value={toy.ratingNo}
                            precision={0.5}
                            readOnly
                            size="small"
                            sx={{
                              "& .MuiRating-iconFilled": {
                                color: "#E53935",
                              },
                            }}
                          />
                        </Box>
                      )}
                    </StyledTooltip>
                  </TableCell>

                  <TableCell sx={{ pt: 2, pb: 0, border: "none" }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-start",
                      }}
                    >
                      {toy.publishedOn === "Draft" ? (
                        <StyledTooltip title="Publish this toy">
                          <IconButton
                            size="small"
                            onClick={() => handlePublishClick(toy.id)}
                            sx={{
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <AssignmentTurnedInOutlined fontSize="small" />
                          </IconButton>
                        </StyledTooltip>
                      ) : (
                        <StyledTooltip title="Unpublish this toy">
                          <IconButton
                            size="small"
                            onClick={() => handleUnpublishClick(toy.id)}
                            sx={{
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <FactCheckOutlined fontSize="small" />
                          </IconButton>
                        </StyledTooltip>
                      )}
                      <StyledTooltip title="View this toy">
                        <IconButton
                          size="small"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <Link
                            component={RouterLink}
                            to={`/toy/${toy.uuid}`}
                            target="_blank"
                            color="black"
                            underline="none"
                          >
                            <VisibilityOutlined fontSize="small" />
                          </Link>
                        </IconButton>
                      </StyledTooltip>
                      <StyledTooltip title="Edit this toy">
                        <IconButton
                          size="small"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <Link
                            component={RouterLink}
                            to={`/toy/${toy.uuid}/edit`}
                            color="black"
                            underline="none"
                            sx={{
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </Link>
                        </IconButton>
                      </StyledTooltip>
                      <StyledTooltip title="Delete this toy">
                        <IconButton
                          size="small"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <DeleteOutlined
                            fontSize="small"
                            onClick={() => handleDeleteToy(toy)}
                          />
                        </IconButton>
                      </StyledTooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* Card bottom padding */}
            <Box sx={{ pb: 2 }} />
          </Box>
        ))}

        {/* Mobile Pagination */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 15, 20]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 1,
                paddingRight: 1,
                minHeight: "52px",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "14px",
                color: "#666",
              },
              "& .MuiTablePagination-select": {
                fontSize: "14px",
              },
              "& .MuiTablePagination-actions": {
                "& .MuiIconButton-root": {
                  padding: "6px",
                },
              },
            }}
          />
        </Box>
        </>
        )}
        <FloatingMenu left={floatMenuLeftMargin} />
      </Box>

      {/* Desktop/Tablet View - Only shows on screens >= 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "100px",
          "@media (max-width: 1360px)": {
            px: "16px",
          },
          "@media (max-width: 833px)": {
            display: "none",
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            height: "91px",
            p: 1,
            pl: 2,
            pr: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "@media (max-width: 833px)": {
                flexDirection: "column",
                gap: 2,
              },
            }}
          >
            {/* Title Section */}
            <Box>
              <Typography
                sx={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                My Toys for Sale
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                Manage and edit your uploaded toys
              </Typography>
            </Box>

            {/* Filter Controls */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                "@media (max-width: 833px)": {
                  width: "100%",
                  justifyContent: "flex-start",
                },
              }}
            >
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                sx={{
                  width: "150px",
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    backgroundColor: "white",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "14px",
                    "&.Mui-focused": {
                      color: "#666",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                }}
                SelectProps={{
                  IconComponent: ArrowDropDown,
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="For Sale">For Sale</MenuItem>
                <MenuItem value="Sold">Sold</MenuItem>
                <MenuItem value="Shipped">Shipped</MenuItem>
                <MenuItem value="Received">Received</MenuItem>
                <MenuItem value="Accepted">Accepted</MenuItem>
                {/* --- Divider --- */}
                <MenuItem disabled sx={{ padding: 0 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "1px",
                      backgroundColor: "rgba(0,0,0,0.12)",
                      margin: "4px 0",
                    }}
                  />
                </MenuItem>

                <MenuItem value="Draft">Draft</MenuItem>
              </TextField>

              {/* <TextField
                  select
                  label="View"
                  value={viewFilter}
                  onChange={handleViewFilterChange}
                  sx={{
                    width: "150px",
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                      backgroundColor: "white",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#666",
                      fontSize: "14px",
                      "&.Mui-focused": {
                        color: "#666",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                  }}
                  SelectProps={{
                    IconComponent: ArrowDropDown,
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Published">Published</MenuItem>
                  <MenuItem value="Draft">Draft</MenuItem>
                </TextField> */}
            </Box>
          </Box>
        </Box>

        {/* Table */}
        {!isLoaded ? (
          <Box p={4} textAlign="center">Loading...</Box>
        ) : isLoaded && toys.length === 0 ? (
          <EmptyState 
            type="listings"
            title="No Toys Available"
            description="You haven't listed any toys yet."
          />
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "white" }}>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  Photo
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "title"}
                    direction={orderBy === "title" ? order : "asc"}
                    onClick={() => handleRequestSort("title")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Title/Summary
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "price"}
                    direction={orderBy === "price" ? order : "asc"}
                    onClick={() => handleRequestSort("price")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "createdOn"}
                    direction={orderBy === "createdOn" ? order : "asc"}
                    onClick={() => handleRequestSort("createdOn")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Created on
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "publishedOn"}
                    direction={orderBy === "publishedOn" ? order : "asc"}
                    onClick={() => handleRequestSort("publishedOn")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Published on
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleRequestSort("status")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                  <TableSortLabel
                    active={orderBy === "ratingNo"}
                    direction={orderBy === "ratingNo" ? order : "asc"}
                    onClick={() => handleRequestSort("ratingNo")}
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      "&.Mui-active": {
                        color: "#333",
                      },
                      "&:hover": {
                        color: "#333",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#333 !important",
                      },
                    }}
                  >
                    Buyer's Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: "#333",
                    py: 2,
                    minWidth: "150px",
                    textAlign: "center",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map(toy => (
                <React.Fragment key={toy.id}>
                  <TableRow
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                      },
                      ...(isTablet && {
                        "& td": {
                          borderBottom: "none",
                        },
                      }),
                    }}
                  >
                    <TableCell sx={{ py: 2, pb: isTablet ? 0 : 2 }}>
                      <Avatar
                        src={toy.photo}
                        alt={toy.title}
                        sx={{
                          width: isTablet ? 40 : 80,
                          height: isTablet ? 40 : 80,
                          borderRadius: "8px",
                        }}
                      />
                    </TableCell>

                    {/* Title Cell - Spans 6 columns on tablet, normal on desktop */}
                    <TableCell
                      colSpan={isTablet ? 6 : 1}
                      sx={{
                        py: isTablet ? 1 : 2,
                        maxWidth: "300px",
                        "@media (max-width: 1360px)": {
                          maxWidth: "none",
                          paddingBottom: 0,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "500",
                          lineHeight: 1.4,
                        }}
                      >
                        {toy.title}
                      </Typography>
                    </TableCell>

                    {/* Desktop columns - hidden on tablet */}
                    {!isTablet && (
                      <>
                        <TableCell sx={{ py: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                fontWeight: "500",
                              }}
                            >
                              {toy.price}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#999",
                                fontWeight: "400",
                                textDecoration: "line-through",
                              }}
                            >
                              {toy.originalPrice}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {toy.createdOn}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {toy.publishedOn === "Draft" ? "" : toy.publishedOn}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box
                            sx={{
                              backgroundColor: "white",
                              color: "#333",
                              border: "1px solid #000",
                              fontSize: "12px",
                              fontWeight: "500",
                              width: "80px",
                              height: "30px",
                              minWidth: "80px",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            {toy.publishedOn === "Draft" ? "Draft" : toy.status}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          {toy.ratingNo > 0 && (
                            <StyledTooltip title={`${toy.ratingNo} of 5`}>
                              <Box sx={{ display: "inline-block" }}>
                                <Rating
                                  value={toy.ratingNo}
                                  precision={0.5}
                                  readOnly
                                  size="small"
                                  sx={{
                                    "& .MuiRating-iconFilled": {
                                      color: "#E53935",
                                    },
                                  }}
                                />
                              </Box>
                            </StyledTooltip>
                          )}
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "center",
                            }}
                          >
                            {/* Conditional first icon based on published status */}
                            {toy.publishedOn === "Draft" ? (
                              <StyledTooltip title="Publish this toy">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePublishClick(toy.id)}
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <AssignmentTurnedInOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                            ) : (
                              <StyledTooltip title="Unpublish this toy">
                                <IconButton
                                  size="small"
                                  onClick={() => handleUnpublishClick(toy.id)}
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <FactCheckOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                            )}
                            <StyledTooltip title="View this toy">
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <Link
                                  component={RouterLink}
                                  to={`/toy/${toy.uuid}`}
                                  target="_blank"
                                  color="black"
                                  underline="none"
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <VisibilityOutlined fontSize="small" />
                                </Link>
                              </IconButton>
                            </StyledTooltip>
                            <StyledTooltip title="Edit this toy">
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <Link
                                  component={RouterLink}
                                  to={`/toy/${toy.uuid}/edit`}
                                  color="black"
                                  underline="none"
                                >
                                  <EditOutlined fontSize="small" />
                                </Link>
                              </IconButton>
                            </StyledTooltip>
                            <StyledTooltip title="Delete this toy">
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <DeleteOutlined
                                  fontSize="small"
                                  onClick={() => handleDeleteToy(toy)}
                                />
                              </IconButton>
                            </StyledTooltip>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>

                  {/* Second row for tablet view */}
                  {isTablet && (
                    <TableRow
                      key={`${toy.id}-details`}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f9f9f9",
                        },
                        "& td": {
                          borderTop: "none",
                        },
                      }}
                    >
                      {/* Empty cell for Photo and Title column */}

                      <TableCell sx={{ py: 0 }}></TableCell>
                      <TableCell sx={{ py: 0 }}></TableCell>

                      <TableCell sx={{ pt: 0, pb: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: "500",
                            }}
                          >
                            {toy.price}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#999",
                              fontWeight: "400",
                              textDecoration: "line-through",
                            }}
                          >
                            {toy.originalPrice}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ pt: 0, pb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          {toy.createdOn}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ pt: 0, pb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          {toy.publishedOn === "Draft" ? "" : toy.publishedOn}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ pt: 0, pb: 2 }}>
                        <Box
                          sx={{
                            backgroundColor: "white",
                            color: "#333",
                            border: "1px solid #000",
                            fontSize: "12px",
                            fontWeight: "500",
                            width: "80px",
                            height: "30px",
                            minWidth: "80px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          {toy.publishedOn === "Draft" ? "Draft" : toy.status}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        {toy.ratingNo > 0 && (
                          <StyledTooltip title={`${toy.ratingNo} of 5`}>
                            <Box sx={{ display: "inline-block" }}>
                              <Rating
                                value={toy.ratingNo}
                                precision={0.5}
                                readOnly
                                size="small"
                                sx={{
                                  "& .MuiRating-iconFilled": {
                                    color: "#E53935",
                                  },
                                }}
                              />
                            </Box>
                          </StyledTooltip>
                        )}
                      </TableCell>

                      <TableCell sx={{ pt: 0, pb: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          {/* Conditional first icon based on published status */}
                          {toy.publishedOn === "Draft" ? (
                            <StyledTooltip title="Publish this toy">
                              <IconButton
                                size="small"
                                onClick={() => handlePublishClick(toy.id)}
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <AssignmentTurnedInOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                          ) : (
                            <StyledTooltip title="Unpublish this toy">
                              <IconButton
                                size="small"
                                onClick={() => handleUnpublishClick(toy.id)}
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <FactCheckOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                          )}
                          <StyledTooltip title="View this toy">
                            <IconButton
                              size="small"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <Link
                                component={RouterLink}
                                to={`/toy/${toy.uuid}`}
                                target="_blank"
                                color="black"
                                underline="none"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <VisibilityOutlined fontSize="small" />
                              </Link>
                            </IconButton>
                          </StyledTooltip>
                          <StyledTooltip title="Edit this toy">
                            <IconButton
                              size="small"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <Link
                                component={RouterLink}
                                to={`/toy/${toy.uuid}/edit`}
                                color="black"
                                underline="none"
                              >
                                <EditOutlined fontSize="small" />
                              </Link>
                            </IconButton>
                          </StyledTooltip>
                          <StyledTooltip title="Delete this toy">
                            <IconButton
                              size="small"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <DeleteOutlined
                                fontSize="small"
                                onClick={() => handleDeleteToy(toy)}
                              />
                            </IconButton>
                          </StyledTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 15, 20]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 2,
                paddingRight: 2,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "14px",
                color: "#666",
              },
              "& .MuiTablePagination-select": {
                fontSize: "14px",
              },
            }}
          />
        </TableContainer>
        )}

        {/* Alert Popup */}
        <AlertPopup
          open={alertPopup.open}
          onClose={handleCancelAction}
          title={alertPopup.type === "publish" ? "Draft to Publish" : "Publish to Draft"}
          message={
            alertPopup.type === "publish"
              ? "Are you ready to publish this toy?"
              : "Are you sure you want to unpublish this toy?"
          }
          confirmText={alertPopup.type === "publish" ? "YES, PUBLISH" : "YES, UNPUBLISH"}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelAction}
        />

        {/* Alert Popup */}
        <AlertPopup
          open={deleteAlertPopup.open}
          onClose={handleDeleteCancelAction}
          title={"Delete"}
          message={"Are you sure you want to delete this toy?"}
          confirmText={"DELETE"}
          onConfirm={handleDeleteToyAction}
          onCancel={handleDeleteCancelAction}
        />

        <AlertPopup
          open={showPopup}
          onClose={() => setShowPopup(false)}
          title={popupConfig.title}
          message={popupConfig.message}
          showCheckCircle={popupConfig.showCheckCircle}
          confirmText={popupConfig.confirmText}
          cancelText={popupConfig.cancelText}
          onConfirm={popupConfig.onConfirm}
        />

        <FloatingMenu left={floatMenuLeftMargin} />
      </Box>
    </>
  );
};

export default ToysList;
